import type { ComponentPublicInstance, Ref } from 'vue'

import { useTimeoutFn } from '@vueuse/core'
import { onBeforeUnmount, ref } from 'vue'

import { prefersReducedMotion } from '@/utils/motion-util/motionUtil'

interface SectionScrollSpy {
  activeSectionId: Ref<string | null>
  registerSection: (id: string, el: Element | ComponentPublicInstance | null) => void
  scrollToSection: (id: string) => void
}

/** Venue bar (56) plus the pinned category bar (~52) — the line a section
 *  heading has to cross before it counts as the one you are in. */
const PINNED_CHROME_PX = 116

/** How long a tap-driven scroll is given to reach its target, so the
 *  observer doesn't briefly re-claim the active state on the way there. */
const JUMP_SETTLE_MS = 600

/**
 * Which menu section the diner is looking at, and jumping to another.
 *
 * Uses a top-biased rootMargin rather than "most visible": on a long menu the
 * section whose heading has just passed under the pinned bar is the one people
 * consider themselves to be in, even while the previous section still fills
 * most of the screen. Measuring by area picks the wrong one for the whole
 * first screenful of every section.
 */
const useSectionScrollSpy = (): SectionScrollSpy => {
  const activeSectionId = ref<string | null>(null)

  const elements = new Map<string, Element>()

  // Suppressed while a tap-driven scroll is in flight, or the sections passing
  // under the observer on the way would each briefly claim the active state.
  let isJumping = false
  const { start: startJumpTimer } = useTimeoutFn(
    () => {
      isJumping = false
    },
    JUMP_SETTLE_MS,
    { immediate: false }
  )

  const observer =
    typeof IntersectionObserver === 'undefined'
      ? null
      : new IntersectionObserver(
          entries => {
            if (isJumping) return

            const entered = entries.filter(entry => entry.isIntersecting)

            if (entered.length === 0) return

            // Topmost of whatever is currently crossing the line.
            const top = entered.reduce((best, entry) =>
              entry.boundingClientRect.top < best.boundingClientRect.top ? entry : best
            )

            activeSectionId.value = top.target.id.replace(/^menu-section-/, '')
          },
          { rootMargin: `-${PINNED_CHROME_PX}px 0px -70% 0px`, threshold: 0 }
        )

  const registerSection = (id: string, el: Element | ComponentPublicInstance | null): void => {
    const node = (el as ComponentPublicInstance | null)?.$el ?? (el as Element | null)

    if (!node || elements.get(id) === node) return

    elements.set(id, node)
    observer?.observe(node)

    activeSectionId.value ??= id
  }

  const scrollToSection = (id: string): void => {
    const node = elements.get(id)

    if (!node) return

    isJumping = true
    activeSectionId.value = id

    node.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' })

    startJumpTimer()
  }

  onBeforeUnmount(() => {
    observer?.disconnect()
  })

  return { activeSectionId, registerSection, scrollToSection }
}

export { useSectionScrollSpy }
